import type { Pendo, PendoUser } from '#shared/pendo'
import { useRuntimeConfig } from '#imports'
import {
  canTrackImmediately,
  flushPendingTrackEvents,
  getClientPendo,
  isClient,
  queueAndScheduleTrackEvent,
  reportPendoError,
  sendTrackEvent,
} from '~/utils/pendo/track-queue'

function isPendoConfigured(config: ReturnType<typeof useRuntimeConfig>) {
  return isPendoEnabled(config.public.pendoEnabled) && !!config.public.pendoApiKey
}

export function isPendoEnabled(value: unknown) {
  return value === true || value === 'true'
}

export function usePendo() {
  const config = useRuntimeConfig()

  const { analytics } = useCookieConsent()
  const { loggedIn } = useUserSession()

  function pendoAction(action: (pendo: Pendo) => void) {
    const pendo = getClientPendo()

    if (isPendoConfigured(config) && pendo) {
      try {
        action(pendo)
      }
      catch (e) {
        reportPendoError(e)
      }
    }
  }

  async function track(eventName: string, metadata?: Record<string, any>): Promise<boolean> {
    if (!isClient() || !isPendoConfigured(config) || !analytics.value || !loggedIn.value) {
      return false
    }

    return new Promise<boolean>((resolve) => {
      const event = { eventName, metadata, queuedAt: Date.now(), resolve }

      try {
        const pendo = getClientPendo()

        if (!pendo) {
          queueAndScheduleTrackEvent(event)
          return
        }

        if (!canTrackImmediately(pendo)) {
          queueAndScheduleTrackEvent(event)
          return
        }

        flushPendingTrackEvents(pendo)
        sendTrackEvent(pendo, event)
      }
      catch (error) {
        reportPendoError(error)
        resolve(false)
      }
    })
  }

  return {
    teardown() {
      pendoAction(pendo => pendo.teardown?.())
    },
    flushNow() {
      pendoAction(pendo => pendo.flushNow?.())
    },
    updateOptions(options: PendoUser) {
      if (!analytics.value || !loggedIn.value) {
        return
      }

      pendoAction(pendo => pendo.updateOptions(options))
    },
    track,
  }
}
